import FishPenLine from '@/assets/icons/fish_pen_line.svg?component';
import InformationLine from '@/assets/icons/information_line.svg?component';
import PathMergeLine from '@/assets/icons/path_merge_line.svg?component';
import PathSplitLine from '@/assets/icons/path_split_line.svg?component';
import type { ConfirmRegistrationItem } from '@/components/organisms/FishGroupForms';
import { getIsoWeek } from '@/utils/date';

import { type BioscopeOutcome, bioscopeOutcomeItems } from './bioscopeOutcomes';
import type { Translate } from './penOptions';

/** What a split or a grading will show once confirmed. */
export function splitConfirmItems(
    summary: {
        kind: 'split' | 'grading';
        date: Date;
        groupName: string;
        newGroups: number;
        /** From `splitBioscopeOutcomes`: every bioscope in a pen the split touches. */
        bioscopes: readonly BioscopeOutcome[];
        /** The group's pen, when no new group stays in it. */
        emptiedPen: string | null;
    },
    t: Translate
): ConfirmRegistrationItem[] {
    return [
        {
            key: 'timeline',
            icon: InformationLine,
            title: t('siteActions.confirm.timeline'),
            text: t(`siteActions.${summary.kind}.marker`, { week: getIsoWeek(summary.date) }),
        },
        {
            key: 'groups',
            icon: PathSplitLine,
            title: t('siteActions.confirm.newGroups', { count: summary.newGroups }),
            text: t('siteActions.confirm.sourceCloses', { name: summary.groupName }),
        },
        ...bioscopeOutcomeItems(summary.bioscopes, t),
        ...(summary.emptiedPen
            ? [
                  {
                      key: 'pen',
                      icon: FishPenLine,
                      title: summary.emptiedPen,
                      text: t('siteActions.confirm.penEmptied'),
                  },
              ]
            : []),
    ];
}

/** What a merge will show once confirmed, the pens it empties included. */
export function mergeConfirmItems(
    summary: {
        date: Date;
        name: string;
        groups: number;
        /** From `mergeBioscopeOutcomes`: the destination's bioscopes and the ones left behind. */
        bioscopes: readonly BioscopeOutcome[];
        emptiedPens: readonly string[];
    },
    t: Translate
): ConfirmRegistrationItem[] {
    const { date, name, groups, bioscopes, emptiedPens } = summary;
    return [
        {
            key: 'timeline',
            icon: InformationLine,
            title: t('siteActions.confirm.timeline'),
            text: t('siteActions.merge.marker', { week: getIsoWeek(date) }),
        },
        {
            key: 'group',
            icon: PathMergeLine,
            title: name,
            text: t('siteActions.merge.groupsCombine', { count: groups }),
        },
        ...bioscopeOutcomeItems(bioscopes, t),
        ...(emptiedPens.length > 0
            ? [
                  {
                      key: 'pens',
                      icon: FishPenLine,
                      title: emptiedPens.join(', '),
                      text: t('siteActions.merge.pensEmptied', { count: emptiedPens.length }),
                  },
              ]
            : []),
    ];
}
